const AMAZON_AFFILIATE_CONFIG = {
  ES: {
    domain: 'www.amazon.es',
    countryName: 'Spain',
    flag: '🇪🇸',
    storeLabel: 'Amazon ES',
    tag: process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_ES || '101setups02-21',
  },
  GB: {
    domain: 'www.amazon.co.uk',
    countryName: 'United Kingdom',
    flag: '🇬🇧',
    storeLabel: 'Amazon UK',
    tag:
      process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_GB ||
      process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_UK ||
      '101setups0f-21',
  },
  US: {
    domain: 'www.amazon.com',
    countryName: 'United States',
    flag: '🇺🇸',
    storeLabel: 'Amazon US',
    tag: process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_US || '101setups-20',
  },
  FR: {
    domain: 'www.amazon.fr',
    countryName: 'France',
    flag: '🇫🇷',
    storeLabel: 'Amazon FR',
    tag: process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_FR || '101setups0e-21',
  },
  DE: {
    domain: 'www.amazon.de',
    countryName: 'Germany',
    flag: '🇩🇪',
    storeLabel: 'Amazon DE',
    tag: process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_DE || '101setups072-21',
  },
  IT: {
    domain: 'www.amazon.it',
    countryName: 'Italy',
    flag: '🇮🇹',
    storeLabel: 'Amazon IT',
    tag: process.env.REACT_APP_AMAZON_ASSOCIATES_TAG_IT || '101setups0ff-21',
  },
};

const REGION_ALIASES = {
  UK: 'GB',
  EN_GB: 'GB',
  ES_ES: 'ES',
  EN_US: 'US',
  FR_FR: 'FR',
  DE_DE: 'DE',
  IT_IT: 'IT',
};

export const AMAZON_SUPPORTED_REGIONS = ['ES', 'GB', 'US', 'FR', 'DE', 'IT'];
export const AMAZON_STORE_PREFERENCE_KEY = 'amazon-store-preference';
export const AMAZON_STORE_UPDATED_EVENT = 'amazon-store-preference-updated';

const dispatchWindowEvent = (eventName, detail = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(eventName, { detail }));
};

export const normalizeAmazonRegion = regionCandidate => {
  if (!regionCandidate) return null;

  const normalized = String(regionCandidate)
    .trim()
    .replace('-', '_')
    .toUpperCase();

  return REGION_ALIASES[normalized] || normalized;
};

export const getStoredAmazonRegion = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedRegion = normalizeAmazonRegion(window.localStorage.getItem(AMAZON_STORE_PREFERENCE_KEY));

  if (!storedRegion || !AMAZON_SUPPORTED_REGIONS.includes(storedRegion)) {
    return null;
  }

  return storedRegion;
};

export const setAmazonStorePreference = regionCandidate => {
  if (typeof window === 'undefined') {
    return null;
  }

  const normalizedRegion = normalizeAmazonRegion(regionCandidate);

  if (!normalizedRegion || !AMAZON_SUPPORTED_REGIONS.includes(normalizedRegion)) {
    return null;
  }

  window.localStorage.setItem(AMAZON_STORE_PREFERENCE_KEY, normalizedRegion);
  dispatchWindowEvent(AMAZON_STORE_UPDATED_EVENT, { region: normalizedRegion, source: 'manual' });

  return normalizedRegion;
};

export const clearAmazonStorePreference = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(AMAZON_STORE_PREFERENCE_KEY);
  dispatchWindowEvent(AMAZON_STORE_UPDATED_EVENT, {
    region: null,
    source: 'browser',
  });
};

export const getBrowserPreferredAmazonRegion = () => {
  if (typeof navigator === 'undefined') {
    return 'ES';
  }

  const localeCandidates = [...(navigator.languages || []), navigator.language, navigator.userLanguage].filter(Boolean);

  for (const locale of localeCandidates) {
    const normalized = normalizeAmazonRegion(locale);
    if (!normalized) continue;

    if (normalized.startsWith('ES')) return 'ES';
    if (normalized.startsWith('GB')) return 'GB';
    if (normalized.startsWith('US')) return 'US';
    if (normalized.startsWith('FR')) return 'FR';
    if (normalized.startsWith('DE')) return 'DE';
    if (normalized.startsWith('IT')) return 'IT';
  }

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timeZone.includes('Madrid')) return 'ES';
    if (timeZone.includes('London')) return 'GB';
    if (timeZone.includes('Paris')) return 'FR';
    if (timeZone.includes('Berlin')) return 'DE';
    if (timeZone.includes('Rome')) return 'IT';
    if (timeZone.startsWith('America/')) return 'US';
  } catch (error) {
    console.error('Error resolving browser timezone for Amazon region:', error);
  }

  return 'ES';
};

export const getAmazonStoreResolution = countryCode => {
  const normalized = normalizeAmazonRegion(countryCode);

  if (normalized && AMAZON_SUPPORTED_REGIONS.includes(normalized)) {
    return { region: normalized, source: 'explicit' };
  }

  const storedRegion = getStoredAmazonRegion();
  if (storedRegion) {
    return { region: storedRegion, source: 'manual' };
  }

  return { region: getBrowserPreferredAmazonRegion(), source: 'browser' };
};

export const getAmazonStoreSelectionSource = countryCode => getAmazonStoreResolution(countryCode).source;

export const resolveAmazonRegion = countryCode => getAmazonStoreResolution(countryCode).region;

export const getAmazonStoreDetails = region => {
  const resolvedRegion = resolveAmazonRegion(region);
  const config = AMAZON_AFFILIATE_CONFIG[resolvedRegion] || AMAZON_AFFILIATE_CONFIG.ES;

  return {
    region: resolvedRegion,
    domain: config.domain,
    countryName: config.countryName,
    flag: config.flag,
    storeLabel: config.storeLabel,
    selectorLabel: `${config.flag} ${config.countryName}`,
    hasAffiliateTag: Boolean(config.tag),
  };
};

export const buildAmazonSearchUrl = ({ region, keywords }) => {
  const resolvedRegion = resolveAmazonRegion(region);
  const config = AMAZON_AFFILIATE_CONFIG[resolvedRegion] || AMAZON_AFFILIATE_CONFIG.ES;
  const url = new URL(`https://${config.domain}/s`);

  url.searchParams.set('k', keywords);

  if (config.tag) {
    url.searchParams.set('tag', config.tag);
  }

  return url.toString();
};

export const hasAffiliateTagForRegion = region => {
  const resolvedRegion = resolveAmazonRegion(region);
  return Boolean((AMAZON_AFFILIATE_CONFIG[resolvedRegion] || {}).tag);
};

export const AMAZON_ASSOCIATE_LINE = 'As an Amazon Associate, we earn from qualifying purchases.';

export const AMAZON_GLOBAL_DISCLOSURE =
  'Some links on 101Setups, including Amazon links, may earn 101Setups a commission at no extra cost to you. Tagged Amazon links may open search results or similar products rather than a verified exact product page.';

export const AMAZON_STORE_DISCLOSURE =
  'Amazon links default to a regional store chosen from your browser settings and time zone. You can change the store from the top navigation, and your selected override is remembered on this device.';
